// Nutzeranfrage: `navigator.onLine` erkennt nur eine fehlende Netzwerk-
// schnittstelle, nicht eine schlechte/langsame Verbindung (z. B. schwaches
// WLAN/Mobilfunk im Supermarkt) - dort blieb z. B. "Kaufen" im Einkaufsmodus
// unbegrenzt hängen, statt in die Offline-Warteschlange zu fallen. `apiFetch`
// meldet daher jeden fehlgeschlagenen/timeout-behafteten Request über
// `reportConnectivityFailure()`, was die App wie "offline" reagieren lässt,
// bis wieder ein Request erfolgreich war oder das Browser-`online`-Event
// feuert. Zusätzlich kann der Offline-Modus manuell erzwungen werden, ohne
// die tatsächliche Internetverbindung des Geräts zu trennen.
const FORCE_OFFLINE_KEY = 'sl-force-offline';

function readForcedOffline(): boolean {
  try {
    return localStorage.getItem(FORCE_OFFLINE_KEY) === 'true';
  } catch {
    return false;
  }
}

let browserOnline = navigator.onLine;
let forcedOffline = readForcedOffline();
let degraded = false;
const listeners = new Set<() => void>();

function notify() {
  for (const l of listeners) l();
}

function computeOnline(): boolean {
  return browserOnline && !forcedOffline && !degraded;
}

export interface ConnectionState {
  online: boolean;
  browserOnline: boolean;
  forcedOffline: boolean;
  degraded: boolean;
}

let state: ConnectionState = { online: computeOnline(), browserOnline, forcedOffline, degraded };
function refreshState(): void {
  state = { online: computeOnline(), browserOnline, forcedOffline, degraded };
}

window.addEventListener('online', () => {
  browserOnline = true;
  degraded = false; // Netzwerk ist zurück - dem nächsten Request wieder eine Chance geben.
  refreshState();
  notify();
});
window.addEventListener('offline', () => {
  browserOnline = false;
  refreshState();
  notify();
});

export const onlineStore = {
  get(): boolean {
    return computeOnline();
  },
  getState(): ConnectionState {
    return state;
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  reportConnectivityFailure(): void {
    if (!degraded) {
      degraded = true;
      refreshState();
      notify();
    }
  },
  reportSuccess(): void {
    if (degraded) {
      degraded = false;
      refreshState();
      notify();
    }
  },
  setForcedOffline(value: boolean): void {
    if (forcedOffline === value) return;
    forcedOffline = value;
    try {
      localStorage.setItem(FORCE_OFFLINE_KEY, String(value));
    } catch {
      /* z. B. privater Modus ohne Storage-Zugriff - Umschaltung bleibt zumindest für die Sitzung gültig. */
    }
    refreshState();
    notify();
  },
};
